import React from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import Input from "@/components/ui/input";
import TextEditor from "@/components/controls/TextEditor/TextEditor";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { ProposalSlot, ProposalSlotKind } from "../types";
import { BlankMarker } from "./BlankMarker";
import { SlotFlagMarker } from "./SlotFlagMarker";
import { SlotFlag } from "./slotFlags";
import { restrictedMarkdownToHtml } from "@/components/controls/TextEditor/utils/restrictedMarkdown";
import { isReadOnly } from "./blanks";
import { SLOT_FORMATS, SLOT_FORMATTED } from "./slotFormats";

const TERMS_AND_SERVICES = "terms_and_services";

// Input's disabled box; the value stays primary, since it is real contract data.
const READ_ONLY_BOX =
  "w-full rounded-lg border-[0.5px] border-border-field-disabled bg-surface-fields-disabled cursor-not-allowed outline-none focus-visible:border-border-field-focus";

interface Props {
  slot: ProposalSlot;
  value: string;
  blank: boolean;
  // What the last composed document could not show as typed.
  flag?: SlotFlag;
  onChange: (slotId: string, value: string) => void;
  // Held while a save or send is on its way; an edit then would be lost.
  locked?: boolean;
  // Shows and tints where the slot sits in the document.
  onLocate?: (slotId: string) => void;
  // The field lost focus.
  onLeave?: (slotId: string) => void;
}

export const SlotField: React.FC<Props> = ({
  slot,
  value,
  blank,
  flag,
  onChange,
  locked = false,
  onLocate,
  onLeave
}) => {
  const { t } = useTranslation();
  const inputId = `proposal-slot-${slot.id}`;
  const labelId = `${inputId}-label`;
  const placeholder = t("contracts.proposals.editor.addValue", {
    label: slot.label.toLowerCase()
  });

  // Why the rep can't type here: the backend's own reason, else where the value is kept.
  const readOnlyHint = () => {
    if (slot.readOnlyNote) return slot.readOnlyNote;
    if (slot.record === TERMS_AND_SERVICES)
      return t("contracts.proposals.editor.editInTermsAndServices");
    if (slot.record)
      return t("contracts.proposals.editor.readOnlyFromRecord", {
        source: t(`contracts.proposals.editor.records.${slot.record}`, {
          defaultValue: slot.record
        })
      });
    return t("contracts.proposals.editor.readOnlyField");
  };

  const renderControl = () => {
    if (isReadOnly(slot)) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            {/* Focusable, so a click or Tab still shows where it sits. */}
            <div
              tabIndex={0}
              role="textbox"
              aria-readonly="true"
              aria-multiline={slot.multiline || undefined}
              aria-labelledby={labelId}
              className={`${READ_ONLY_BOX} min-h-9 px-3 py-2`}
              onFocus={() => onLocate?.(slot.id)}
              onBlur={() => onLeave?.(slot.id)}
              data-test={`${inputId}-readonly`}
            >
              {slot.multiline ? (
                <Text as="div" variant="body">
                  <div
                    className={SLOT_FORMATTED}
                    // The parser escapes any HTML in the value.
                    dangerouslySetInnerHTML={{
                      __html: restrictedMarkdownToHtml(value, SLOT_FORMATS)
                    }}
                  />
                </Text>
              ) : (
                <Text variant="body">{value}</Text>
              )}
            </div>
          </TooltipTrigger>
          <TooltipContent>{readOnlyHint()}</TooltipContent>
        </Tooltip>
      );
    }
    if (slot.multiline) {
      // Whether focus moved across the field's edge, not within it (its toolbar).
      const crossesField = (event: React.FocusEvent<HTMLDivElement>) =>
        !event.currentTarget.contains(event.relatedTarget);
      return (
        <div
          onFocus={(event) => crossesField(event) && onLocate?.(slot.id)}
          onBlur={(event) => crossesField(event) && onLeave?.(slot.id)}
        >
          <TextEditor
            variant="form"
            value={value}
            placeholder={placeholder}
            formats={SLOT_FORMATS}
            maxLength={slot.maxLength}
            disabled={locked}
            ariaLabelledBy={labelId}
            onChange={(next) => onChange(slot.id, next)}
          />
        </div>
      );
    }
    return (
      <Input
        id={inputId}
        value={value}
        placeholder={placeholder}
        maxLength={slot.maxLength}
        readOnly={locked}
        onFocus={() => onLocate?.(slot.id)}
        onBlur={() => onLeave?.(slot.id)}
        onChange={(event) => onChange(slot.id, event.target.value)}
      />
    );
  };

  return (
    <div className="flex flex-col gap-1" data-test={inputId}>
      <div className="flex items-center gap-1 min-h-6">
        <Text
          as="label"
          id={labelId}
          htmlFor={isReadOnly(slot) || slot.multiline ? undefined : inputId}
          variant="bodySecondary"
          className="flex-1 pl-1"
        >
          {slot.label}
        </Text>
        {blank ? <BlankMarker missing={isReadOnly(slot)} /> : null}
        {flag ? <SlotFlagMarker flag={flag} /> : null}
        {slot.kind === ProposalSlotKind.DATA && slot.record ? (
          <Text
            variant="smBodySecondary"
            className="shrink-0"
            data-test={`${inputId}-source`}
          >
            {t("contracts.proposals.editor.source", {
              source: t(`contracts.proposals.editor.records.${slot.record}`, {
                defaultValue: slot.record
              })
            })}
          </Text>
        ) : null}
      </div>
      {renderControl()}
    </div>
  );
};
